import React, { memo, useEffect, useState, useRef } from 'react';
import './index.less';
import { Modal, Input, App } from 'antd';
import UploadImage from '@/components/upload-img';
import queryDomMethods from '@/utils/queryDomMethods';
import globalStore from '@/store/global';
import { getPredictScoreFeedbackType, savePredictScoreFeedback } from '@/apis';
import { IPredictScoreFeedbackType } from '@/types';
import { areKeysPresentAndNonEmpty } from '@/utils';
import classNames from 'classnames';

const { TextArea } = Input;

const PredictScoreFeedback = () => {
    const { message } = App.useApp();
    const feedBackTypeInitInfo = useRef({
        qrCode: '',
        typeList: [],
    });
    const [initInfo, setInitInfo] = useState<{
        projectId: number;
        subjectId: number;
        examSeasonId: number;
        examSyllabusId?: number;
        sectionId?: number;
    }>();
    const [feedbackTypes, setFeedbackTypes] = useState<IPredictScoreFeedbackType>(feedBackTypeInitInfo.current);
    const [isModalOpen, setIsModalOpen] = useState(false);
    const [content, setContent] = useState('');
    const [feedbackType, setFeedbackType] = useState(0);
    const [imageList, setImageList] = useState<string[]>([]); // 图片
    const [valid, setValid] = useState<boolean>(false);

    const fetchFeedBackType = async () => {
        const response = await getPredictScoreFeedbackType({
            projectId: initInfo!.projectId,
            subjectId: initInfo!.subjectId,
            sectionId: initInfo!.sectionId,
            examSyllabusId: initInfo!.examSyllabusId,
        });
        if (response.status === 0 && response.result) {
            setFeedbackTypes(response.result);
        }
    };

    const onChangeContent = (e: any) => {
        setContent(e.target.value);
    };

    const onChangeImage = (uploadImgs: string[]) => {
        setImageList(uploadImgs);
    };

    const handleFeedbackTypeChange = (id: number) => {
        setFeedbackType(id);
    };

    const handleSubmit = async () => {
        if (!content || !feedbackType) {
            setValid(true);
            return;
        }
        setValid(false);
        const { projectId, subjectId, examSeasonId, examSyllabusId, sectionId } = initInfo!;
        const response = await savePredictScoreFeedback({
            projectId,
            subjectId,
            examSeasonId,
            sectionId,
            examSyllabusId,
            content: content,
            feedbackType: feedbackType,
            imagesList: imageList,
        });
        if (response.status === 0) {
            handleCancelModal();
            message.success('感谢您的反馈，尽快为您处理');
        } else {
            message.error(response.message);
        }
    };

    const handleCancelModal = () => {
        setValid(false);
        setIsModalOpen(false);
    };

    useEffect(() => {
        if (isModalOpen) {
            fetchFeedBackType();
        } else {
            setInitInfo(undefined);
            setContent('');
            setFeedbackType(0);
            setImageList([]);
            setFeedbackTypes(feedBackTypeInitInfo.current);
        }
    }, [isModalOpen]);

    useEffect(() => {
        globalStore.subscribe('PredictScoreFeedback', ({ type, params }) => {
            switch (type) {
                case 'open':
                    if (areKeysPresentAndNonEmpty(params, ['projectId', 'subjectId', 'examSeasonId'])) {
                        setInitInfo(params);
                        setIsModalOpen(true);
                    } else {
                        console.error('Invalid params for PredictScoreFeedback');
                    }
                    break;
                default:
                    setInitInfo(undefined);
                    break;
            }
        });
        return () => {
            globalStore.unsubscribe('PredictScoreFeedback');
        };
    }, []);
    return (
        <>
            <Modal
                wrapClassName="sub-tiku__predict-score-feedback"
                title="意见反馈"
                footer={
                    <>
                        <div className="sub-tiku__tiku-default-btn" onClick={handleCancelModal}>
                            取消
                        </div>
                        <div className="sub-tiku__tiku-default-btn sub-tiku__tiku-plain-btn" onClick={handleSubmit}>
                            提交
                        </div>
                    </>
                }
                width={560}
                open={isModalOpen}
                onCancel={handleCancelModal}
                destroyOnClose
                getContainer={queryDomMethods.querySubTikuRoot}
            >
                <div className="sub-tiku__predict-score-feedback-title">
                    反馈类型 <span>*</span>
                </div>
                <div className="sub-tiku__predict-score-feedback-checkbox">
                    {feedbackTypes.typeList.map(item => (
                        <div
                            key={item.id}
                            className={classNames('sub-tiku__tiku-default-btn sub-tiku__predict-score-feedback-checkbox-btn', {
                                active: item.id === feedbackType,
                            })}
                            onClick={() => handleFeedbackTypeChange(item.id)}
                        >
                            {item.name}
                        </div>
                    ))}
                </div>
                <TextArea
                    status={valid ? 'error' : ''}
                    className="sub-tiku__predict-score-feedback-textarea"
                    showCount
                    maxLength={300}
                    value={content}
                    onChange={onChangeContent}
                    placeholder="请留下你的问题反馈或优化建议，我们高度重视每位用户的体验价值，将有专职团队跟进处理。"
                    style={{ height: 100, resize: 'none' }}
                />
                {valid && <div className="sub-tiku__predict-score-feedback-error">请输入反馈类型或者内容！</div>}
                <UploadImage imageList={imageList} maxFileCount={5} maxFileSize={4} onChangeImage={onChangeImage} />
                <div className="sub-tiku__predict-score-feedback-QR-code">
                    <img src={feedbackTypes.qrCode} alt="" />
                </div>
            </Modal>
        </>
    );
};

export default memo(PredictScoreFeedback);
